/*
 * @Author: Claude
 * @Date: 2026-01-06
 * @Description: 解析和格式化宽高比工具函数
 */

/**
 * 解析宽高比字符串或数字为数值
 * @param ratio - 宽高比，支持数字或字符串格式
 *                数字: 如 0.4, 1.0, 2.5
 *                字符串: 如 '1:2.5', '16:9', '4:3'
 * @returns number - 宽高比数值（宽度/高度）
 * @example
 * parseAspectRatio(0.4) // => 0.4
 * parseAspectRatio('1:2.5') // => 0.4
 * parseAspectRatio('16:9') // => 1.777...
 * parseAspectRatio('2.5:1') // => 2.5
 */
export const parseAspectRatio = (ratio: number | string): number => {
  // 如果已经是数字，直接返回
  if (typeof ratio === 'number') {
    return ratio;
  }

  // 解析 '宽:高' 格式，如 '16:9', '1:2.5'
  const match = ratio.match(/^(\d+(?:\.\d+)?)\s*:\s*(\d+(?:\.\d+)?)$/);
  if (match) {
    const [_, width, height] = match;
    const widthNum = parseFloat(width);
    const heightNum = parseFloat(height);

    // 避免除以零
    if (heightNum === 0) {
      throw new Error(`Invalid aspect ratio: ${ratio} (height cannot be 0)`);
    }

    return widthNum / heightNum;
  }

  // 尝试直接解析为数字（兜底）
  const parsed = parseFloat(ratio);
  if (isNaN(parsed)) {
    throw new Error(`Invalid aspect ratio format: ${ratio}`);
  }

  return parsed;
};

/**
 * 格式化宽高比为友好的显示字符串
 * @param ratio - 宽高比数值
 * @param precision - 小数精度，默认 2 位
 * @returns string - 格式化后的字符串，如 '1.78:1', '0.56:1'
 * @example
 * formatAspectRatio(1.7777) // => '1.78:1'
 * formatAspectRatio(0.5625) // => '0.56:1'
 * formatAspectRatio(2.5) // => '2.50:1'
 */
export const formatAspectRatio = (ratio: number, precision: number = 2): string => {
  return `${ratio.toFixed(precision)}:1`;
};

/**
 * 格式化宽高比为分数形式
 * @param ratio - 宽高比数值
 * @returns string - 格式化后的分数字符串，如 '16:9', '4:3', '1:1'
 * @example
 * formatAspectRatioAsFraction(1.7777) // => '16:9'
 * formatAspectRatioAsFraction(1.3333) // => '4:3'
 * formatAspectRatioAsFraction(1.0) // => '1:1'
 * formatAspectRatioAsFraction(0.5625) // => '9:16'
 */
export const formatAspectRatioAsFraction = (ratio: number): string => {
  // 常见宽高比映射表
  const commonRatios: { [key: string]: string } = {
    '0.56': '9:16', // 竖屏手机
    '0.75': '3:4', // 竖屏平板
    '1.00': '1:1', // 正方形
    '1.33': '4:3', // 传统显示器
    '1.50': '3:2', // 经典照片
    '1.78': '16:9', // 现代显示器/电视
    '2.00': '2:1', // 宽屏
    '2.17': '19.5:9', // 现代手机
    '2.35': '21:9', // 电影
    '2.50': '5:2', // 超宽屏
  };

  const roundedRatio = ratio.toFixed(2);
  return commonRatios[roundedRatio] || `${ratio.toFixed(2)}:1`;
};
